//
// Copyright 2026 DXOS.org
//

import * as Effect from 'effect/Effect';
import * as Schema from 'effect/Schema';
import * as Migrator from 'effect/sql/Migrator';
import * as SqlClient from 'effect/sql/SqlClient';
import type * as SqlError from 'effect/sql/SqlError';

import * as SpanAttributes from '@dxos/effect/SpanAttributes';
import { SpaceId } from '@dxos/keys';

import { MIGRATIONS, MIGRATIONS_TABLE } from './migrations/tracker/index.ts';
import { chunkArray } from './utils.ts';

export const IndexCursor = Schema.Struct({
  /**
   * Name of the index owning this cursor.
   */
  indexName: Schema.String,
  /**
   * Space id.
   */
  spaceId: Schema.NullOr(SpaceId),
  /**
   * Source name.
   * 'automerge' / 'queue' / 'index' (for secondary indexes)
   */
  sourceName: Schema.String,
  /**
   * Document id or queue id.
   * doc_id, queue_id, '' <empty string> (if indexing entire namespace)
   */
  resourceId: Schema.NullOr(Schema.String),
  /**
   * Heads, queue position, version.
   */
  cursor: Schema.Union([Schema.Number, Schema.String]),
});
export interface IndexCursor extends Schema.Schema.Type<typeof IndexCursor> {}

export class IndexTracker {
  readonly #sql: SqlClient.SqlClient;

  constructor(sql: SqlClient.SqlClient) {
    this.#sql = sql;
  }

  /**
   * Applies any migrations this database has not recorded yet.
   */
  migrate = Effect.fn('IndexTracker.migrate')(() =>
    Migrator.make({})({ loader: Migrator.fromRecord(MIGRATIONS), table: MIGRATIONS_TABLE }).pipe(
      // A malformed bundled manifest is a defect, not something a caller can recover from.
      Effect.catchTag('MigrationError', (error) => Effect.die(error)),
      Effect.asVoid,
      Effect.provideService(SqlClient.SqlClient, this.#sql),
    ),
  );

  queryCursors = Effect.fn('IndexTracker.queryCursors')(
    (
      query: Pick<IndexCursor, 'indexName'> & Partial<Pick<IndexCursor, 'sourceName' | 'resourceId' | 'spaceId'>>,
    ): Effect.Effect<IndexCursor[], SqlError.SqlError> =>
      Effect.gen({ self: this }, function* () {
        const sql = this.#sql;
        if (query.spaceId) {
          yield* Effect.annotateCurrentSpan(SpanAttributes.SPACE_ID, query.spaceId);
        }

        const spaceIdParam = query.spaceId === undefined ? null : (query.spaceId ?? '');
        const sourceNameParam = query.sourceName === undefined ? null : query.sourceName;
        const resourceIdParam = query.resourceId === undefined ? null : (query.resourceId ?? '');

        const rows = yield* sql<IndexCursor>`
            SELECT * FROM indexCursor 
            WHERE indexName = ${query.indexName}
            AND (${spaceIdParam} IS NULL OR spaceId = ${spaceIdParam})
            AND (${sourceNameParam} IS NULL OR sourceName = ${sourceNameParam})
            AND (${resourceIdParam} IS NULL OR resourceId = ${resourceIdParam})
        `;

        return rows.map((row): IndexCursor => ({
          indexName: row.indexName,
          spaceId: row.spaceId === '' ? null : Schema.decodeSync(SpaceId)(row.spaceId!),
          sourceName: row.sourceName,
          resourceId: row.resourceId === '' ? null : row.resourceId,
          cursor: row.cursor,
        }));
      }),
  );

  /**
   * Cursors for every index of one source, keyed by `indexName`. `IndexEngine.update` refreshes all
   * of a source's indexes together, so querying per index re-scans `indexCursor` once per index for
   * a result the caller can partition itself.
   */
  queryCursorsBySource = Effect.fn('IndexTracker.queryCursorsBySource')(
    (query: {
      sourceName: string;
      spaceId?: SpaceId | null;
    }): Effect.Effect<Map<string, IndexCursor[]>, SqlError.SqlError> =>
      Effect.gen({ self: this }, function* () {
        const sql = this.#sql;

        const spaceIdParam = query.spaceId === undefined ? null : (query.spaceId ?? '');

        const rows = yield* sql<IndexCursor>`
            SELECT * FROM indexCursor
            WHERE sourceName = ${query.sourceName}
            AND (${spaceIdParam} IS NULL OR spaceId = ${spaceIdParam})
        `;

        const byIndex = new Map<string, IndexCursor[]>();
        for (const row of rows) {
          const cursor: IndexCursor = {
            indexName: row.indexName,
            spaceId: row.spaceId === '' ? null : Schema.decodeSync(SpaceId)(row.spaceId!),
            sourceName: row.sourceName,
            resourceId: row.resourceId === '' ? null : row.resourceId,
            cursor: row.cursor,
          };
          const existing = byIndex.get(row.indexName);
          if (existing) {
            existing.push(cursor);
          } else {
            byIndex.set(row.indexName, [cursor]);
          }
        }
        return byIndex;
      }),
  );

  updateCursors = Effect.fn('IndexTracker.updateCursors')(
    (cursors: IndexCursor[]): Effect.Effect<void, SqlError.SqlError> =>
      Effect.gen({ self: this }, function* () {
        const sql = this.#sql;
        yield* Effect.forEach(
          cursors,
          (cursor) => {
            const spaceId = cursor.spaceId ?? '';
            const resourceId = cursor.resourceId ?? '';
            return sql`
            INSERT INTO indexCursor (indexName, spaceId, sourceName, resourceId, cursor)
            VALUES (${cursor.indexName}, ${spaceId}, ${cursor.sourceName}, ${resourceId}, ${cursor.cursor})
            ON CONFLICT(indexName, spaceId, sourceName, resourceId) DO UPDATE SET cursor = excluded.cursor
          `;
          },
          { discard: true },
        );
      }),
  );

  /** Delete cursors for documents (resource ids) wiped by garbage collection. */
  deleteCursors = Effect.fn('IndexTracker.deleteCursors')(
    (query: { spaceId: SpaceId; resourceIds: readonly string[] }): Effect.Effect<void, SqlError.SqlError> =>
      Effect.gen({ self: this }, function* () {
        const sql = this.#sql;
        for (const chunk of chunkArray(query.resourceIds)) {
          yield* sql`DELETE FROM indexCursor WHERE spaceId = ${query.spaceId} AND ${sql.in('resourceId', chunk)}`;
        }
      }),
  );
}
